import React, { useState, useEffect } from 'react'
import {
  Table,
  Input,
  Button,
  Space,
  Card,
  Statistic,
  Row,
  Col,
  message,
  Modal,
  Form,
  InputNumber,
  Drawer,
  Descriptions,
  Tag,
  Divider,
  Empty,
  Spin,
  Select,
  Popconfirm,
  DatePicker
} from 'antd'
import {
  SearchOutlined,
  EditOutlined,
  EyeOutlined,
  DollarOutlined,
  ShoppingOutlined,
  WalletOutlined,
  DeleteOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import {
  MobileRecordList,
  MobileRecordCard,
  MobileCardPagination
} from '../components/common/MobileRecordCard'
import { useAuth } from '../hooks/AuthContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../utils/api'
import dayjs from 'dayjs'
import 'dayjs/locale/zh-tw'
import zhTW from 'antd/es/date-picker/locale/zh_TW'
import { formatTz } from '../utils/timezone'

// 設置 dayjs 為中文
dayjs.locale('zh-tw')

const { Search } = Input
const { Option } = Select

const PageContainer = styled.div`
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`

const ActionButtons = styled(Space)`
  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
  }
`

const PrimaryButton = styled(Button)`
  border-radius: 8px;
  font-weight: 500;
  height: 40px;
  padding: 0 20px;
  background: #7c3aed;
  border-color: #7c3aed;
  color: #fff;

  &:hover {
    background: #6d28d9;
    border-color: #6d28d9;
  }

  @media (max-width: 768px) {
    height: 36px;
    padding: 0 16px;
    font-size: 14px;
  }
`

const SearchContainer = styled.div`
  margin-bottom: 24px;

  .ant-input-affix-wrapper {
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 8px 16px;
    font-size: 14px;

    &:hover,
    &:focus {
      border-color: #7c3aed;
      box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.1);
    }
  }

  .ant-input-search-button {
    border-radius: 0 12px 12px 0;
    background: #7c3aed;
    border-color: #7c3aed;

    &:hover {
      background: #6d28d9;
      border-color: #6d28d9;
    }
  }
`

const SummaryCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
  border: none;

  .ant-card-body {
    padding: 24px;
  }

  .ant-statistic-title {
    color: #6b7280;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .ant-statistic-content {
    color: #1a1a1a;
    font-size: 24px;
    font-weight: 600;
  }
`

const TableCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: none;

  .ant-card-body {
    padding: 0;
  }

  .ant-table {
    .ant-table-thead > tr > th {
      background: #f9fafb;
      border-bottom: 2px solid #e5e7eb;
      font-weight: 600;
      color: #374151;
      padding: 16px;
      text-align: center;

      &:first-child {
        border-radius: 12px 0 0 0;
      }

      &:last-child {
        border-radius: 0 12px 0 0;
      }
    }

    .ant-table-tbody > tr > td {
      padding: 16px;
      text-align: center;
      border-bottom: 1px solid #f3f4f6;
    }

    .ant-table-tbody > tr:hover > td {
      background: #f9fafb;
    }

    .ant-table-tbody > tr:last-child > td {
      border-bottom: none;
    }
  }
`

/* 儲值類型 → 標籤顏色 */
const typeColor = (type) => {
  if (type === '購買') return 'green'
  if (type === '轉讓') return 'blue'
  if (type === '儲值') return 'orange'
  if (type === '扣款') return 'red'
  if (type === '退款') return 'volcano'
  if (type === '調整') return 'purple'
  return 'default'
}

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 24px;
  }
`

const DetailSection = styled.div`
  margin-bottom: 24px;

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e5e7eb;
  }
`

const ConsumptionItem = styled.div`
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 8px;

  .item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .item-name {
    font-weight: 500;
    color: #1a1a1a;
  }

  .item-amount {
    font-weight: 600;
    color: #ef4444;
  }

  .item-meta {
    font-size: 12px;
    color: #6b7280;
  }
`

const ValueAddedRecordsPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const [searchParams] = useSearchParams()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
  const customerId = searchParams.get('customerId') // 從 URL 參數獲取 customerId
  const recordId = searchParams.get('recordId') // 從 URL 參數獲取 recordId
  const [records, setRecords] = useState([])
  const [summary, setSummary] = useState({
    purchaseAmount: 0,
    totalValueAdded: 0,
    totalRemaining: 0
  })
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })
  // 依日期排序，購買與扣款穿插在同一時間軸：sortField=bookingDate&sortOrder=descend
  // 扣款用 bookingDate（預約日），購買用 date（銷售日，等同預約日），兩者合併由新到舊（descend）
  const [sortedInfo, setSortedInfo] = useState({
    field: 'bookingDate',
    order: 'descend'
  })

  // 查詢篩選：按月、按類型（後端需支援 startDate, endDate, type 參數）
  const [filterMonth, setFilterMonth] = useState(null) // dayjs 或 null（全部）
  const [filterType, setFilterType] = useState(undefined) // undefined=全部 | 'purchase' | 'transfer' | 'topup'

  // Modal 和 Drawer 狀態
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const [consumptionRecords, setConsumptionRecords] = useState([])
  const [loadingConsumption, setLoadingConsumption] = useState(false)
  const [employees, setEmployees] = useState([])


  useEffect(() => {
    fetchEmployees()
    fetchRecords()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.current,
    pagination.pageSize,
    sortedInfo.field,
    sortedInfo.order,
    searchText,
    customerId,
    recordId,
    filterMonth,
    filterType
  ])

  // 獲取員工列表
  const fetchEmployees = async () => {
    if (!user?.businessId) return
    try {
      const response = await api.get(`/api/employees/${user.businessId}`)
      setEmployees(response.data || [])
    } catch (error) {
      console.error('獲取員工列表失敗:', error)
    }
  }

  const fetchRecords = async () => {
    setLoading(true)
    try {
      const params = {
        page: pagination.current,
        pageSize: pagination.pageSize,
        search: searchText || undefined,
        // 固定依預約日＋銷售日時間軸由新到舊排序（不依賴 Table 的 sortedInfo，避免被其他欄位點擊覆蓋）
        sortField: 'bookingDate',
        sortOrder: 'descend'
      }

      // 如果有 customerId 參數，添加到請求中
      if (customerId) {
        params.customerId = customerId
      }

      // 如果有 recordId 參數，添加到請求中
      if (recordId) {
        params.recordId = recordId
      }

      // 按月查詢：傳 startDate, endDate（後端需支援）
      if (filterMonth) {
        const start = dayjs(filterMonth).startOf('month')
        const end = dayjs(filterMonth).endOf('month')
        params.startDate = start.format('YYYY-MM-DD')
        params.endDate = end.format('YYYY-MM-DD')
      }

      // 按類型查詢：傳 type（後端需支援 purchase | transfer | topup | deduct）
      if (filterType) {
        params.type = filterType
      }

      const response = await api.get(
        `/api/businesses/${user.businessId}/value-added-records`,
        {
          params
        }
      )

      // 處理 API 響應數據
      const data = response.data
      if (data.success !== false) {
        // 解析響應數據 - 支持兩種格式：{ data: { records: [...] } } 或 { records: [...] }
        const recordsData = data.data?.records || data.records || []

        // 映射 API 數據到表格需要的格式（含扣款 deduct）
        const mappedRecords = recordsData.map((record) => {
          const isDeduct =
            record.type === 'deduct' || record.source === 'deduct'
          // 根據 source、type、isTransfer 映射 type
          let type = '購買'
          if (isDeduct) {
            type = '扣款'
          } else if (record.isTransfer === true) {
            type = '轉讓'
          } else if (record.source === 'purchase') {
            type = '購買'
          } else if (record.source === 'topup') {
            if (record.note && record.note.includes('轉讓')) {
              type = '轉讓'
            } else {
              type = '儲值'
            }
          } else if (record.type === 'refund') {
            type = '退款'
          } else if (record.type === 'adjustment') {
            type = '調整'
          } else if (record.type) {
            type = record.type
          }

          // 扣款：金額為 record.amount，顯示用負值
          const deductAmount = isDeduct ? Number(record.amount || 0) : 0
          const totalValueAdded = isDeduct
            ? -deductAmount
            : (record.totalValueAdded ??
              Number(record.valueAddedAmount || 0) +
                Number(record.bonusAmount || 0))

          // 補結帳判斷：僅扣款、bookingDate 存在時。
          // bookingDate 是店家時區的牆上日期，所以 createdAt 也要換成店家時區
          // 再比，否則台灣凌晨結的帳會被誤判成「補結帳」。
          const actualCheckoutDate =
            record.createdAt != null
              ? formatTz(record.createdAt, 'YYYY-MM-DD', null)
              : null
          const isBackdatedCheckout =
            isDeduct &&
            record.bookingDate &&
            actualCheckoutDate &&
            record.bookingDate < actualCheckoutDate

          return {
            _id: record._id,
            customerId: record.customerId,
            salesDate: record.date || record.createdAt || record.salesDate,
            customerName: record.customerName,
            phone: record.phone || record.customerPhone,
            salesPersonnel: record.salesPersonnel,
            type,
            source: record.source,
            schemeKey: record.schemeKey,
            isTransfer: record.isTransfer,
            isDeduct,
            paymentMethod: record.paymentMethod,
            purchaseAmount: isDeduct
              ? (record.orderReceivable ?? record.amount)
              : (record.totalPayment ??
                record.actualChargeAmount ??
                record.purchaseAmount),
            valueAddedAmount: record.valueAddedAmount,
            bonusAmount: record.bonusAmount ?? 0,
            totalValueAdded,
            discount: record.discount ?? 100,
            currentBalance: record.currentBalance ?? 0,
            remainingBalance:
              record.balanceAfterValueAdded ??
              record.remainingBalance ??
              record.balanceAfter ??
              0,
            paymentDetails: record.paymentDetails ?? {},
            note: record.note || record.remarks || '',
            createdAt: record.createdAt,
            createdBy: record.createdBy,
            transactionId: record.transactionId,
            orderId: record.orderId,
            orderStatus: record.orderStatus ?? null,
            bookingDate: record.bookingDate ?? null,
            bookingTime: record.bookingTime ?? null,
            actualCheckoutDate: isDeduct ? actualCheckoutDate : null,
            isBackdatedCheckout: isDeduct ? isBackdatedCheckout : false
          }
        })

        setRecords(mappedRecords)

        // 設置匯總數據 - 支持兩種格式
        const summaryData = data.data?.summary || data.summary || {}
        setSummary({
          purchaseAmount: summaryData.purchaseAmount || 0,
          totalValueAdded: summaryData.totalValueAdded || 0,
          totalRemaining:
            summaryData.totalRemaining || summaryData.totalRemainingBalance || 0
        })

        // 設置分頁信息 - 優先使用 API 返回的 pagination 對象
        if (data.pagination) {
          setPagination({
            current: data.pagination.page || pagination.current,
            pageSize: data.pagination.pageSize || pagination.pageSize,
            total: data.total || data.pagination.total || mappedRecords.length
          })
        } else {
          const totalData = data.data?.total || data.total
          setPagination({
            ...pagination,
            total: totalData || mappedRecords.length
          })
        }
      } else {
        message.error(data.message || '獲取儲值記錄失敗')
        setRecords([])
        setSummary({
          purchaseAmount: 0,
          totalValueAdded: 0,
          totalRemaining: 0
        })
        setPagination({
          ...pagination,
          total: 0
        })
      }
    } catch (error) {
      console.error('獲取儲值記錄失敗:', error)
      message.error('獲取儲值記錄失敗，請稍後再試')
      setRecords([])
      setSummary({
        purchaseAmount: 0,
        totalValueAdded: 0,
        totalRemaining: 0
      })
      setPagination({
        ...pagination,
        total: 0
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (value) => {
    setSearchText(value)
    setPagination({ ...pagination, current: 1 })
    // 搜尋時一律依預約日＋銷售日時間軸由新到舊排序
    setSortedInfo({ field: 'bookingDate', order: 'descend' })
  }

  const handleTableChange = (paginationConfig, filters, sorter) => {
    // 若用戶取消排序，使用默認：bookingDate descend（預約日＋銷售日時間軸，新到舊）
    setSortedInfo({
      field: sorter.field || 'bookingDate',
      order: sorter.order || 'descend'
    })
    setPagination({
      ...pagination,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    })
  }

  // 打開修改 Modal
  const handleEdit = (record) => {
    setSelectedRecord(record)
    // 找到銷售人員的 ID（如果有的話）
    const salesPersonnelId = employees.find(
      (emp) => emp.name === record.salesPersonnel
    )?._id
    form.setFieldsValue({
      salesDate: record.salesDate ? dayjs(record.salesDate) : null,
      valueAddedAmount: record.valueAddedAmount,
      bonusAmount: record.bonusAmount || 0,
      discount: record.discount,
      totalPayment: record.purchaseAmount,
      note: record.note,
      salesPersonnel: salesPersonnelId || record.salesPersonnel || null
    })
    setEditModalVisible(true)
  }

  // 刪除儲值記錄
  const handleDelete = async (record) => {
    try {
      setLoading(true)
      await api.delete(
        `/api/businesses/${user.businessId}/value-added/purchase/${record._id}`
      )
      message.success('儲值記錄已刪除')
      fetchRecords()
    } catch (error) {
      console.error('刪除儲值記錄失敗:', error)
      message.error(
        error.response?.data?.message || '刪除儲值記錄失敗，請稍後再試'
      )
    } finally {
      setLoading(false)
    }
  }

  // 提交修改
  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      // 獲取銷售人員名稱
      const salesPersonnelName =
        employees.find((emp) => emp._id === values.salesPersonnel)?.name ||
        values.salesPersonnel ||
        null

      // 格式化日期為 YYYY-MM-DD
      const salesDate = values.salesDate
        ? values.salesDate.format('YYYY-MM-DD')
        : selectedRecord.salesDate

      await api.put(
        `/api/businesses/${user.businessId}/value-added/purchase/${selectedRecord._id}`,
        {
          date: salesDate, // 銷售日期
          salesDate: salesDate, // 同時傳送兩個欄位名稱以兼容後端
          valueAddedAmount: values.valueAddedAmount,
          bonusAmount: values.bonusAmount || 0,
          discount: values.discount,
          totalPayment: values.totalPayment,
          note: values.note,
          salesPersonnel: salesPersonnelName
        }
      )

      message.success('儲值記錄修改成功')
      setEditModalVisible(false)
      form.resetFields()
      fetchRecords()
    } catch (error) {
      console.error('修改儲值記錄失敗:', error)
      message.error(error.response?.data?.message || '修改儲值記錄失敗')
    } finally {
      setLoading(false)
    }
  }

  // 打開詳情 Drawer
  const handleViewDetail = async (record) => {
    setSelectedRecord(record)
    setDetailDrawerVisible(true)
    // 獲取客戶消費記錄
    await fetchConsumptionRecords(record.customerId)
  }

  // 獲取客戶消費記錄
  const fetchConsumptionRecords = async (customerId) => {
    if (!customerId) {
      setConsumptionRecords([])
      return
    }

    setLoadingConsumption(true)
    try {
      const response = await api.get(
        `/api/businesses/${user.businessId}/customers/${customerId}/value-added-consumption`
      )

      const data = response.data.data || response.data
      setConsumptionRecords(data.records || [])
    } catch (error) {
      console.error('獲取消費記錄失敗:', error)
      setConsumptionRecords([])
    } finally {
      setLoadingConsumption(false)
    }
  }

  const columns = [
    {
      title: '項次',
      dataIndex: 'index',
      key: 'index',
      width: 80,
      render: (_, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1
    },
    {
      title: '銷售日期',
      dataIndex: 'salesDate',
      key: 'salesDate',
      sorter: true,
      sortOrder: sortedInfo.field === 'salesDate' && sortedInfo.order,
      render: (date) => {
        if (!date) return '-'
        try {
          return dayjs(date).format('YYYY/MM/DD')
        } catch (e) {
          return date
        }
      }
    },
    {
      title: '客戶',
      dataIndex: 'customerName',
      key: 'customerName',
      sorter: true,
      sortOrder: sortedInfo.field === 'customerName' && sortedInfo.order,
      render: (text) => text || '-'
    },
    {
      title: '電話',
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => text || '-'
    },
    {
      title: '銷售人員',
      dataIndex: 'salesPersonnel',
      key: 'salesPersonnel',
      sorter: true,
      sortOrder: sortedInfo.field === 'salesPersonnel' && sortedInfo.order,
      render: (text) => text || '-'
    },
    {
      title: '類型',
      dataIndex: 'type',
      key: 'type',
      render: (type, record) => {
        let color = 'default'
        if (type === '購買') color = 'green'
        else if (type === '轉讓') color = 'blue'
        else if (type === '儲值') color = 'orange'
        else if (type === '扣款') color = 'red'
        else if (type === '退款') color = 'volcano'
        else if (type === '調整') color = 'purple'
        else color = 'default'
        const hasScheme = record.schemeKey && (type === '儲值' || type === '退款')
        return (
          <Space size={6} wrap>
            <Tag color={color}>{type || '-'}</Tag>
            {hasScheme && (
              <Tag color='purple'>儲值活動：{record.schemeKey}</Tag>
            )}
          </Space>
        )
      }
    },
    {
      title: '儲值金額',
      dataIndex: 'totalValueAdded',
      key: 'totalValueAdded',
      sorter: true,
      sortOrder: sortedInfo.field === 'totalValueAdded' && sortedInfo.order,
      render: (amount, record) => {
        if (amount == null && !record.isDeduct) return '-'
        const num = Number(amount)
        if (record.type === '扣款') {
          return (
            <span style={{ color: '#cf1322' }}>
              -NT$ {Math.abs(num).toLocaleString()}
            </span>
          )
        }
        return amount != null ? `NT$ ${Number(amount).toLocaleString()}` : '-'
      },
      align: 'right'
    },
    {
      title: '預約日期／時間',
      key: 'bookingDateTime',
      width: 130,
      render: (_, record) => {
        if (record.type !== '扣款') return '-'
        const date = record.bookingDate
        const time = record.bookingTime
        if (!date && !time) return <span style={{ color: '#999' }}>—</span>
        return [date, time].filter(Boolean).join(' ') || '—'
      }
    },
    {
      title: '訂單狀態',
      key: 'orderStatus',
      width: 120,
      render: (_, record) => {
        if (record.type !== '扣款') return '-'
        const status = record.orderStatus
        const statusTag = !status ? null : status === 'voided' ? (
          <Tag color='default'>已作廢</Tag>
        ) : status === 'paid' ? (
          <Tag color='green'>已付款</Tag>
        ) : (
          <Tag>{String(status)}</Tag>
        )
        const backdated = record.isBackdatedCheckout && (
          <div style={{ marginTop: 4, fontSize: 12, color: '#d48806' }}>
            <Tag color='orange'>補結帳</Tag>
          </div>
        )
        return (
          <div>
            {statusTag ?? <span style={{ color: '#999' }}>—</span>}
            {backdated}
          </div>
        )
      }
    },
    {
      title: '購買金額',
      dataIndex: 'purchaseAmount',
      key: 'purchaseAmount',
      sorter: true,
      sortOrder: sortedInfo.field === 'purchaseAmount' && sortedInfo.order,
      render: (amount) => (amount ? `NT$ ${amount.toLocaleString()}` : '-'),
      align: 'right'
    },
    {
      title: '餘額',
      dataIndex: 'remainingBalance',
      key: 'remainingBalance',
      render: (amount, record) => {
        if (record.type === '扣款') {
          const before = Number(record.currentBalance ?? 0)
          const after = Number(record.remainingBalance ?? 0)
          return (
            <span style={{ whiteSpace: 'nowrap' }}>
              NT$ {after.toLocaleString()}
            </span>
          )
        }
        return amount ? `NT$ ${Number(amount).toLocaleString()}` : '-'
      },
      align: 'right'
    },
    {
      title: '操作',
      key: 'action',
      width: 130,
      fixed: 'right',
      render: (_, record) => (
        <Space size='small'>
          <Button
            type='link'
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
            title='查看詳情'
          />
          {record.type !== '扣款' && (
            <>
              <Button
                type='link'
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
                title='編輯'
              />
              <Popconfirm
                title='確定要刪除此儲值記錄嗎？'
                description='刪除後將無法恢復，請確認此操作。'
                onConfirm={() => handleDelete(record)}
                okText='確定'
                cancelText='取消'
                okButtonProps={{ danger: true }}
              >
                <Button
                  type='link'
                  danger
                  icon={<DeleteOutlined />}
                  title='刪除'
                />
              </Popconfirm>
            </>
          )}
        </Space>
      )
    }
  ]

  // 手機版：把表格轉成卡片清單，方便閱讀
  const renderMobileCards = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      )
    }
    if (!records || records.length === 0) {
      return (
        <div style={{ padding: 24 }}>
          <Empty description='暫無儲值記錄' />
        </div>
      )
    }
    return (
      <>
        <MobileRecordList $padded>
          {records.map((record, index) => {
            const isDeduct = record.type === '扣款'
            const seq =
              (pagination.current - 1) * pagination.pageSize + index + 1
            const salesDate = record.salesDate
              ? (() => {
                  try {
                    return dayjs(record.salesDate).format('YYYY/MM/DD')
                  } catch (e) {
                    return record.salesDate
                  }
                })()
              : '-'
            const valueAddedText = (() => {
              if (record.totalValueAdded == null && !record.isDeduct)
                return '-'
              const num = Number(record.totalValueAdded)
              if (isDeduct) return `-NT$ ${Math.abs(num).toLocaleString()}`
              return record.totalValueAdded != null
                ? `NT$ ${num.toLocaleString()}`
                : '-'
            })()
            return (
              <MobileRecordCard
                key={record._id}
                title={record.customerName || '-'}
                subtitle={`#${seq}　${salesDate}`}
                extra={
                  <>
                    <Tag color={typeColor(record.type)} style={{ marginRight: 0 }}>
                      {record.type || '-'}
                    </Tag>
                    {record.schemeKey &&
                      (record.type === '儲值' || record.type === '退款') && (
                        <Tag color='purple' style={{ marginRight: 0 }}>
                          活動：{record.schemeKey}
                        </Tag>
                      )}
                  </>
                }
                rows={[
                  { label: '電話', value: record.phone, hidden: !record.phone },
                  {
                    label: '銷售人員',
                    value: record.salesPersonnel || '-'
                  },
                  {
                    label: isDeduct ? '扣款金額' : '儲值金額',
                    value: valueAddedText,
                    valueStyle: isDeduct ? { color: '#cf1322' } : undefined
                  },
                  {
                    label: '購買金額',
                    value: record.purchaseAmount
                      ? `NT$ ${record.purchaseAmount.toLocaleString()}`
                      : '-',
                    hidden: isDeduct
                  },
                  {
                    label: '餘額',
                    value:
                      record.remainingBalance != null
                        ? `NT$ ${Number(
                            record.remainingBalance
                          ).toLocaleString()}`
                        : '-'
                  },
                  {
                    label: '預約日期／時間',
                    value: [record.bookingDate, record.bookingTime]
                      .filter(Boolean)
                      .join(' '),
                    hidden:
                      !isDeduct || (!record.bookingDate && !record.bookingTime)
                  },
                  {
                    label: '訂單狀態',
                    hidden: !isDeduct || !record.orderStatus,
                    value: (
                      <>
                        {record.orderStatus === 'voided' ? (
                          <Tag color='default'>已作廢</Tag>
                        ) : record.orderStatus === 'paid' ? (
                          <Tag color='green'>已付款</Tag>
                        ) : (
                          <Tag>{String(record.orderStatus)}</Tag>
                        )}
                        {record.isBackdatedCheckout ? (
                          <Tag color='orange'>補結帳</Tag>
                        ) : null}
                      </>
                    )
                  }
                ]}
                actions={
                  <>
                    <Button
                      size='small'
                      icon={<EyeOutlined />}
                      onClick={() => handleViewDetail(record)}
                    >
                      詳情
                    </Button>
                    {record.type !== '扣款' && (
                      <>
                        <Button
                          size='small'
                          icon={<EditOutlined />}
                          onClick={() => handleEdit(record)}
                        >
                          編輯
                        </Button>
                        <Popconfirm
                          title='確定要刪除此儲值記錄嗎？'
                          description='刪除後將無法恢復，請確認此操作。'
                          onConfirm={() => handleDelete(record)}
                          okText='確定'
                          cancelText='取消'
                          okButtonProps={{ danger: true }}
                        >
                          <Button size='small' danger icon={<DeleteOutlined />}>
                            刪除
                          </Button>
                        </Popconfirm>
                      </>
                    )}
                  </>
                }
              />
            )
          })}
        </MobileRecordList>
        <MobileCardPagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onChange={(page, pageSize) =>
            handleTableChange({ current: page, pageSize }, {}, sortedInfo || {})
          }
        />
      </>
    )
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>儲值記錄查詢</PageTitle>
        <ActionButtons>
          <PrimaryButton
            icon={<WalletOutlined />}
            onClick={() => navigate('/value-added/remaining-balance')}
          >
            剩餘儲值金查詢
          </PrimaryButton>
        </ActionButtons>
      </PageHeader>

      <SearchContainer>
        <Row gutter={[16, 16]} align='middle'>
          <Col xs={24} sm={24} md={8}>
            <label
              style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}
            >
              搜尋
            </label>
            <Search
              placeholder='搜尋客戶名稱、電話或其他資訊'
              allowClear
              enterButton={<SearchOutlined />}
              size='large'
              onSearch={handleSearch}
              onChange={(e) => {
                if (!e.target.value) {
                  setSearchText('')
                }
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <label
              style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}
            >
              按月查詢
            </label>
            <DatePicker
              picker='month'
              format='YYYY-MM'
              placeholder='全部月份'
              style={{ width: '100%' }}
              value={filterMonth}
              onChange={(date) => {
                setFilterMonth(date || null)
                setPagination((p) => ({ ...p, current: 1 }))
              }}
              locale={zhTW}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <label
              style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}
            >
              按類型查詢
            </label>
            <Select
              style={{ width: '100%' }}
              placeholder='全部類型'
              allowClear
              value={filterType}
              onChange={(val) => {
                setFilterType(val)
                setPagination((p) => ({ ...p, current: 1 }))
              }}
              options={[
                { value: 'purchase', label: '購買' },
                { value: 'transfer', label: '轉讓' },
                { value: 'topup', label: '儲值' },
                { value: 'deduct', label: '扣款' }
              ]}
            />
          </Col>
          <Col xs={24} sm={24} md={4}>
            <Button
              size='large'
              onClick={() => {
                setFilterMonth(null)
                setFilterType(undefined)
                setPagination((p) => ({ ...p, current: 1 }))
              }}
              style={{ width: '100%', marginTop: 30 }}
            >
              清除篩選
            </Button>
          </Col>
        </Row>
      </SearchContainer>

      <SummaryCard>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={8}>
            <Statistic
              title='購買金額'
              value={summary.purchaseAmount}
              prefix='NT$'
              valueStyle={{ color: '#7c3aed' }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title='總儲值金額'
              value={summary.totalValueAdded}
              prefix='NT$'
              valueStyle={{ color: '#7c3aed' }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title='總剩餘儲值金額'
              value={summary.totalRemaining}
              prefix='NT$'
              valueStyle={{ color: '#10b981' }}
            />
          </Col>
        </Row>
      </SummaryCard>

      <TableCard>
        {isMobile ? (
          renderMobileCards()
        ) : (
          <Table
            columns={columns}
            dataSource={records}
            rowKey='_id'
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 筆記錄`,
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
          />
        )}
      </TableCard>

      {/* 修改儲值金額 Modal */}
      <Modal
        title='修改儲值記錄'
        open={editModalVisible}
        onOk={handleEditSubmit}
        onCancel={() => {
          setEditModalVisible(false)
          form.resetFields()
        }}
        confirmLoading={loading}
        okText='確認修改'
        cancelText='取消'
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            name='salesDate'
            label='銷售日期'
            rules={[{ required: true, message: '請選擇銷售日期' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format='YYYY-MM-DD'
              placeholder='請選擇銷售日期'
              locale={zhTW}
            />
          </Form.Item>

          <Form.Item
            name='valueAddedAmount'
            label='購買金額（計入業績）'
            extra='顧客實際購買/支付的金額，業績以此計算（不含贈送）'
            rules={[
              { required: true, message: '請輸入購買金額' },
              { type: 'number', min: 1, message: '購買金額必須大於 0' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={(value) => {
                if (!value && value !== 0) return ''
                const numStr = String(value).replace(/\D/g, '')
                if (!numStr) return ''
                return `NT$ ${numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
              }}
              parser={(value) => {
                if (!value) return undefined
                const numStr = value.replace(/NT\$\s?|,/g, '').trim()
                if (numStr === '') return undefined
                const num = Number(numStr)
                return isNaN(num) ? undefined : num
              }}
              min={1}
              controls={false}
            />
          </Form.Item>

          <Form.Item
            name='bonusAmount'
            label='贈送金額'
            extra='活動贈送的金額，會計入儲值餘額但不計業績（例：買三萬送一萬，此處填 10,000）'
            rules={[
              { type: 'number', min: 0, message: '贈送金額不可為負' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={(value) => {
                if (!value && value !== 0) return ''
                const numStr = String(value).replace(/\D/g, '')
                if (!numStr) return ''
                return `NT$ ${numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
              }}
              parser={(value) => {
                if (!value) return undefined
                const numStr = value.replace(/NT\$\s?|,/g, '').trim()
                if (numStr === '') return undefined
                const num = Number(numStr)
                return isNaN(num) ? undefined : num
              }}
              min={0}
              controls={false}
            />
          </Form.Item>

          <Form.Item
            name='discount'
            label='折扣百分比'
            rules={[
              { required: true, message: '請輸入折扣百分比' },
              {
                type: 'number',
                min: 0,
                max: 100,
                message: '折扣必須在 0-100 之間'
              }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={100}
              formatter={(value) => {
                if (!value && value !== 0) return ''
                return `${value}%`
              }}
              parser={(value) => {
                if (!value) return undefined
                const numStr = value.replace('%', '').trim()
                if (numStr === '') return undefined
                const num = Number(numStr)
                return isNaN(num) ? undefined : num
              }}
              controls={false}
            />
          </Form.Item>

          <Form.Item
            name='totalPayment'
            label='總付款金額'
            rules={[
              { required: true, message: '請輸入總付款金額' },
              { type: 'number', min: 1, message: '總付款金額必須大於 0' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              formatter={(value) => {
                if (!value && value !== 0) return ''
                const numStr = String(value).replace(/\D/g, '')
                if (!numStr) return ''
                return `NT$ ${numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
              }}
              parser={(value) => {
                if (!value) return undefined
                const numStr = value.replace(/NT\$\s?|,/g, '').trim()
                if (numStr === '') return undefined
                const num = Number(numStr)
                return isNaN(num) ? undefined : num
              }}
              min={1}
              controls={false}
            />
          </Form.Item>

          <Form.Item name='note' label='備註'>
            <Input.TextArea rows={3} placeholder='請輸入備註' />
          </Form.Item>

          <Form.Item name='salesPersonnel' label='銷售人員'>
            <Select
              style={{ width: '100%' }}
              placeholder='請選擇銷售人員'
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              allowClear
            >
              {employees.map((emp) => (
                <Option key={emp._id} value={emp._id} label={emp.name}>
                  {emp.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 詳情 Drawer */}
      <StyledDrawer
        title='儲值記錄詳情'
        placement='right'
        width={600}
        open={detailDrawerVisible}
        onClose={() => {
          setDetailDrawerVisible(false)
          setSelectedRecord(null)
          setConsumptionRecords([])
        }}
      >
        {selectedRecord && (
          <>
            <DetailSection>
              <div className='section-title'>
                <DollarOutlined style={{ marginRight: 8 }} />
                儲值資訊
              </div>
              <Descriptions column={1} bordered>
                <Descriptions.Item label='客戶名稱'>
                  {selectedRecord.customerName}
                </Descriptions.Item>
                <Descriptions.Item label='客戶電話'>
                  {selectedRecord.phone}
                </Descriptions.Item>
                <Descriptions.Item label='銷售日期'>
                  {dayjs(selectedRecord.salesDate).format('YYYY年MM月DD日')}
                </Descriptions.Item>
                <Descriptions.Item label='銷售人員'>
                  {selectedRecord.salesPersonnel}
                </Descriptions.Item>
                <Descriptions.Item label='類型'>
                  {(() => {
                    const type = selectedRecord.type
                    let color = 'default'
                    if (type === '購買') color = 'green'
                    else if (type === '轉讓') color = 'blue'
                    else if (type === '儲值') color = 'orange'
                    else if (type === '扣款') color = 'red'
                    else color = 'default'
                    const hasScheme = selectedRecord.schemeKey && (type === '儲值' || type === '退款')
                    return (
                      <Space size={6} wrap>
                        <Tag color={color}>{type || '-'}</Tag>
                        {hasScheme && (
                          <Tag color='purple'>儲值活動：{selectedRecord.schemeKey}</Tag>
                        )}
                      </Space>
                    )
                  })()}
                </Descriptions.Item>
                {selectedRecord.type === '扣款' &&
                  (selectedRecord.bookingDate ||
                    selectedRecord.bookingTime) && (
                    <Descriptions.Item label='預約日期／時間'>
                      {[selectedRecord.bookingDate, selectedRecord.bookingTime]
                        .filter(Boolean)
                        .join(' ')}
                    </Descriptions.Item>
                  )}
                {selectedRecord.type === '扣款' &&
                  selectedRecord.orderStatus != null && (
                    <Descriptions.Item label='訂單狀態'>
                      {selectedRecord.orderStatus === 'voided' ? (
                        <Tag color='default'>已作廢</Tag>
                      ) : selectedRecord.orderStatus === 'paid' ? (
                        <Tag color='green'>已付款</Tag>
                      ) : (
                        String(selectedRecord.orderStatus)
                      )}
                    </Descriptions.Item>
                  )}
                {selectedRecord.type === '扣款' &&
                  selectedRecord.isBackdatedCheckout && (
                    <>
                      <Descriptions.Item label='補結帳'>
                        <Tag color='orange'>補結帳</Tag>
                      </Descriptions.Item>
                      {selectedRecord.actualCheckoutDate && (
                        <Descriptions.Item label='實際結帳日'>
                          {selectedRecord.actualCheckoutDate}
                        </Descriptions.Item>
                      )}
                    </>
                  )}
                <Descriptions.Item
                  label={
                    selectedRecord.type === '扣款' ? '扣款金額' : '儲值金額'
                  }
                >
                  {selectedRecord.type === '扣款' ? (
                    <span style={{ color: '#cf1322' }}>
                      -NT${' '}
                      {Math.abs(
                        Number(
                          selectedRecord.totalValueAdded ??
                            selectedRecord.amount ??
                            0
                        )
                      ).toLocaleString()}
                    </span>
                  ) : (
                    <>
                      NT${' '}
                      {(
                        selectedRecord.totalValueAdded ??
                        selectedRecord.valueAddedAmount
                      )?.toLocaleString()}
                    </>
                  )}
                </Descriptions.Item>
                {selectedRecord.type !== '扣款' &&
                  selectedRecord.bonusAmount != null &&
                  Number(selectedRecord.bonusAmount) > 0 && (
                    <Descriptions.Item label='贈送金額'>
                      NT$ {Number(selectedRecord.bonusAmount).toLocaleString()}
                    </Descriptions.Item>
                  )}
                {selectedRecord.type !== '扣款' && (
                  <Descriptions.Item label='折扣'>
                    {selectedRecord.discount ?? 100}%
                  </Descriptions.Item>
                )}
                <Descriptions.Item
                  label={
                    selectedRecord.type === '扣款' ? '訂單應收' : '購買金額'
                  }
                >
                  NT${' '}
                  {(
                    selectedRecord.purchaseAmount ??
                    selectedRecord.orderReceivable ??
                    selectedRecord.totalPayment
                  )?.toLocaleString() ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    selectedRecord.type === '扣款' ? '扣款前餘額' : '儲值前餘額'
                  }
                >
                  NT${' '}
                  {Number(selectedRecord.currentBalance ?? 0).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    selectedRecord.type === '扣款' ? '扣款後餘額' : '儲值後餘額'
                  }
                >
                  NT${' '}
                  {Number(
                    selectedRecord.remainingBalance ?? 0
                  ).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label='付款方式'>
                  {selectedRecord.paymentMethod}
                </Descriptions.Item>
                {selectedRecord.paymentDetails && (
                  <>
                    {selectedRecord.paymentDetails.cash > 0 && (
                      <Descriptions.Item label='現金'>
                        NT${' '}
                        {selectedRecord.paymentDetails.cash.toLocaleString()}
                      </Descriptions.Item>
                    )}
                    {selectedRecord.paymentDetails.bankTransfer > 0 && (
                      <Descriptions.Item label='匯款'>
                        NT${' '}
                        {selectedRecord.paymentDetails.bankTransfer.toLocaleString()}
                      </Descriptions.Item>
                    )}
                    {selectedRecord.paymentDetails.accountsReceivable > 0 && (
                      <Descriptions.Item label='應收款'>
                        NT${' '}
                        {selectedRecord.paymentDetails.accountsReceivable.toLocaleString()}
                      </Descriptions.Item>
                    )}
                    {selectedRecord.paymentDetails.mobileCardSwipe > 0 && (
                      <Descriptions.Item label='店內刷卡'>
                        NT${' '}
                        {selectedRecord.paymentDetails.mobileCardSwipe.toLocaleString()}
                      </Descriptions.Item>
                    )}
                  </>
                )}
                {selectedRecord.note && (
                  <Descriptions.Item label='備註'>
                    {selectedRecord.note}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </DetailSection>

            <Divider />

            <DetailSection>
              <div className='section-title'>
                <ShoppingOutlined style={{ marginRight: 8 }} />
                客戶消費記錄
              </div>
              {loadingConsumption ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <Spin size='large' />
                </div>
              ) : consumptionRecords.length > 0 ? (
                consumptionRecords.map((item, index) => (
                  <ConsumptionItem key={index}>
                    <div className='item-header'>
                      <span className='item-name'>
                        {item.serviceName || item.itemName || '服務項目'}
                      </span>
                      <span className='item-amount'>
                        -NT$ {item.amount?.toLocaleString() || 0}
                      </span>
                    </div>
                    <div className='item-meta'>
                      {item.date && (
                        <span>
                          日期：{dayjs(item.date).format('YYYY/MM/DD')}
                        </span>
                      )}
                      {item.time && (
                        <span style={{ marginLeft: 16 }}>
                          時間：{item.time}
                        </span>
                      )}
                      {item.servicePersonnel && (
                        <span style={{ marginLeft: 16 }}>
                          服務人員：{item.servicePersonnel}
                        </span>
                      )}
                    </div>
                  </ConsumptionItem>
                ))
              ) : (
                <Empty description='暫無消費記錄' />
              )}
            </DetailSection>
          </>
        )}
      </StyledDrawer>
    </PageContainer>
  )
}

export default ValueAddedRecordsPage
